// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import clsx from "clsx";
import { Form, Input, Switch } from "antd";
import { toast } from "react-hot-toast";
import { CloseOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";

// Local Imports
import Label from "../../../../components/label/label.component";
import CustomInput from "../../../../components/custom-input/custom-input.component";
import { LOADING, LOGOUT } from "../../../../store/actions";
import instance from "../../../../store/axios";
import CorrectImage from "../../../../assets/correct.svg";

// Style Imports
import "./edit-profile.scss";
import { encryptPassword } from "src/utils/helper/functions";
import { useLocation, useNavigate } from "react-router-dom";

interface EditProfileProps {
  onCancel: () => void;
  onConfirm: () => void;
  edit?: boolean;
  selectedUser?: any;
}

const EditProfile: React.FC<EditProfileProps> = ({
  onCancel,
  onConfirm,
  edit = false,
  selectedUser,
}: EditProfileProps): ReactElement => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitLoginForm, setSubmitLoginForm] = useState(false);
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const [isActive, setIsActive] = useState<boolean>(
    selectedUser?.isActive ?? true
  );
  const [passwordRoleOne, setPasswordRoleOne] = useState<boolean>(false);
  const [passwordRoleTwo, setPasswordRoleTwo] = useState<boolean>(false);
  const [passwordRoleThree, setPasswordRoleThree] = useState<boolean>(false);

  useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmitLoginForm(true);
      },
      () => {
        setSubmitLoginForm(false);
      }
    );
  }, [values]);

  const onSubmit = async (values: any) => {
    values.isActive = isActive ? true : false;

    dispatch({ type: LOADING, payload: true });

    values.password = encryptPassword(values.password);
    values.oldPassword = encryptPassword(values.oldPassword);

    if (!values.password) {
      delete values.password;
    }
    if (!values.oldPassword) {
      delete values.oldPassword;
    }
    try {
      await instance.post(`/${process.env.REACT_APP_ENDPOINT_VERSION}/admin/update`, values);
      toast.success("Your profile updated successfully");
      dispatch({ type: LOGOUT });
      localStorage.removeItem("star_prev_url");
      navigate("/login", { state: location.pathname });
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
    }

    dispatch({ type: LOADING, payload: false });
  };

  const onPasswordChange = (event: any) => {
    const lowerCaseRegex = /[a-z]/;
    const upperCaseRegex = /[A-Z]/;
    const numberRegex = /\d/;
    const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;

    if (event.target.value?.length >= 8) {
      setPasswordRoleOne(true);
    } else {
      setPasswordRoleOne(false);
    }

    if (
      lowerCaseRegex.test(event.target.value) &&
      upperCaseRegex.test(event.target.value)
    ) {
      setPasswordRoleTwo(true);
    } else {
      setPasswordRoleTwo(false);
    }

    if (
      numberRegex.test(event.target.value) &&
      specialCharRegex.test(event.target.value)
    ) {
      setPasswordRoleThree(true);
    } else {
      setPasswordRoleThree(false);
    }
  };

  return (
    <div className="add-edit-user-container flex flex-col w-full px-5 md:px-10 box-border">
      <h3 className=" text-lg md:text-2xl font-semibold m-0">
        {`Edit profile: ${selectedUser?.name}`}
      </h3>
      {!edit && (
        <p className=" text-blue-200 text-base font-medium">
          Please fill out the following fields to create a new user
        </p>
      )}
      <Form
        form={form}
        onFinish={onSubmit}
        scrollToFirstError={true}
        initialValues={{
          name: selectedUser?.name,
          email: selectedUser?.email,
          isActive: selectedUser?.isActive ? true : false,
        }}
      >
        <div className="flex justify-between flex-wrap border-t-[1px] border-gray-100 mt-5 pt-5">
          <div className="flex flex-col w-full md:w-1/2 border-0 md:border-e-[1px] border-gray-100 pe-5 box-border mb-5 md:mb-0">
            <div className="flex flex-col w-full">
              <Label text="Full name" />
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Please input user full name!",
                  },
                ]}
              >
                <CustomInput placeholder="Enter user full name" />
              </Form.Item>
            </div>
            <div className="flex flex-col w-full">
              <Label text="Email" />
              <Form.Item
                name="email"
                rules={[
                  {
                    required: true,
                    message: "Please input your email!",
                  },
                  {
                    type: "email",
                    message: "Please enter valid email!",
                  },
                ]}
              >
                <CustomInput
                  disabled={edit}
                  placeholder="Enter your email address"
                />
              </Form.Item>
            </div>
            <div className="flex flex-col w-full">
              <Label text="Old password" />
              <Form.Item
                name="oldPassword"
                rules={[
                  {
                    required: true,
                    message: "Please input your old password!",
                  },
                ]}
              >
                <Input.Password
                  className="input-field-container"
                  type="password"
                  placeholder="Enter your old password"
                  autoComplete="new-password"
                />
              </Form.Item>
            </div>
            <div className="flex flex-col w-full">
              <Label text="Password" />
              <Form.Item
                name="password"
                rules={[
                  {
                    required: selectedUser?.id ? false : true,
                    message: "Please input your password!",
                  },
                ]}
              >
                <Input.Password
                  className="input-field-container"
                  type="password"
                  placeholder="Enter your password"
                  onChange={onPasswordChange}
                  autoComplete="new-password"
                />
              </Form.Item>
              {form.isFieldTouched("password") && (
                <span
                  className={clsx(
                    "flex items-center",
                    !passwordRoleOne ? "text-red-500" : "text-green-100"
                  )}
                >
                  {passwordRoleOne ? (
                    <img className=" me-1" src={CorrectImage} alt="" />
                  ) : (
                    <CloseOutlined className="me-1" />
                  )}{" "}
                  contains at least 8 characters
                </span>
              )}
              {form.isFieldTouched("password") && (
                <span
                  className={clsx(
                    "flex items-center",
                    !passwordRoleTwo ? "text-red-500" : "text-green-100"
                  )}
                >
                  {passwordRoleTwo ? (
                    <img className="me-1" src={CorrectImage} alt="" />
                  ) : (
                    <CloseOutlined className="me-1" />
                  )}{" "}
                  contain both lower & upper case letters
                </span>
              )}
              {form.isFieldTouched("password") && (
                <span
                  className={clsx(
                    "flex items-center",
                    !passwordRoleThree ? "text-red-500" : "text-green-100"
                  )}
                >
                  {passwordRoleThree ? (
                    <img className="me-1" src={CorrectImage} alt="" />
                  ) : (
                    <CloseOutlined className="me-1" />
                  )}{" "}
                  contains at least one number & a special character
                </span>
              )}
            </div>
            <div className="flex flex-col w-full mt-5">
              <Label text="Status" />
              <Form.Item name="isActive">
                <div className="switch flex items-center">
                  <span className=" me-1">Active </span>
                  <Switch
                    disabled
                    checked={isActive}
                    onChange={(checked: boolean) => setIsActive(checked)}
                  />{" "}
                  <span className="ms-1">Deactive</span>
                </div>
              </Form.Item>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end w-full mt-9">
          <div className="flex justify-between items-center w-full md:w-1/2">
            <button
              type="button"
              onClick={onCancel}
              className="border-[1px] rounded-xl py-3 border-blue-500 text-blue-500 w-[49%] font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="border-[1px] rounded-xl border-blue-500 bg-blue-500 text-white w-[49%] py-3 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              disabled={
                !submitLoginForm ||
                !passwordRoleOne ||
                !passwordRoleTwo ||
                !passwordRoleThree
              }
            >
              Confirm
            </button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default EditProfile;
